import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, User, LogIn } from 'lucide-react';
import { Button } from './ui/button';
import { Logo } from './Logo';
import { useAuth } from '../context/AuthContext';

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Projects', path: '/projects' },
  ];

  const isActive = (path) => location.pathname === path;

  const scrollToQuote = () => {
    if (location.pathname === '/') {
      const quoteSection = document.getElementById('quote-form');
      if (quoteSection) {
        quoteSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      window.location.href = '/#quote-form';
    }
  };

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'glass border-b border-slate-100 shadow-sm' 
          : 'bg-transparent'
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" data-testid="navbar-logo">
            <Logo />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link font-body text-sm font-medium transition-colors ${
                  isActive(link.path) 
                    ? 'text-brand-navy' 
                    : 'text-brand-charcoal hover:text-brand-navy'
                }`}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <a 
              href="tel:512-563-4371" 
              className="flex items-center gap-2 text-brand-charcoal hover:text-brand-navy transition-colors"
              data-testid="phone-link"
            >
              <Phone className="w-4 h-4" />
              <span className="font-body text-sm font-medium">512-563-4371</span>
            </a>
            
            {isAuthenticated ? (
              <Link to="/dashboard">
                <Button 
                  variant="outline"
                  className="border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white font-body font-medium rounded-full px-4"
                  data-testid="dashboard-btn"
                >
                  <User className="w-4 h-4 mr-2" />
                  {user?.name?.split(' ')[0] || 'Dashboard'}
                </Button>
              </Link>
            ) : (
              <Link to="/login">
                <Button 
                  variant="outline"
                  className="border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white font-body font-medium rounded-full px-4"
                  data-testid="login-btn"
                >
                  <LogIn className="w-4 h-4 mr-2" />
                  Member Login
                </Button>
              </Link>
            )}
            
            <Button 
              onClick={scrollToQuote}
              className="bg-brand-navy hover:bg-brand-action text-white font-body font-medium rounded-full px-6 btn-shine"
              data-testid="get-quote-btn"
            >
              Get a Quote
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-brand-navy"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden glass border-t border-slate-100 mobile-menu-enter" data-testid="mobile-menu">
            <div className="py-4 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block px-4 py-3 font-body text-base font-medium rounded-lg transition-colors ${
                    isActive(link.path)
                      ? 'bg-slate-50 text-brand-navy'
                      : 'text-brand-charcoal hover:bg-slate-50'
                  }`}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              ))}
              
              <div className="px-4 pt-4 space-y-3 border-t border-slate-100">
                <a 
                  href="tel:512-563-4371" 
                  className="flex items-center gap-2 text-brand-charcoal"
                  data-testid="mobile-phone-link"
                >
                  <Phone className="w-4 h-4" />
                  <span className="font-body text-sm font-medium">512-563-4371</span>
                </a>
                
                {isAuthenticated ? (
                  <Link to="/dashboard" className="block">
                    <Button 
                      variant="outline"
                      className="w-full border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white font-body font-medium rounded-full"
                      data-testid="mobile-dashboard-btn"
                    >
                      <User className="w-4 h-4 mr-2" />
                      My Dashboard
                    </Button>
                  </Link>
                ) : (
                  <Link to="/login" className="block">
                    <Button 
                      variant="outline"
                      className="w-full border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white font-body font-medium rounded-full"
                      data-testid="mobile-login-btn"
                    >
                      <LogIn className="w-4 h-4 mr-2" />
                      Member Login
                    </Button>
                  </Link>
                )}
                
                <Button 
                  onClick={scrollToQuote}
                  className="w-full bg-brand-navy hover:bg-brand-action text-white font-body font-medium rounded-full"
                  data-testid="mobile-get-quote-btn"
                >
                  Get a Free Quote
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
